/**
 * TRADE 定制：把 Country/region 选择器接到 Hextom TMS 的币种切换上
 *
 * 【要解决什么】
 * 主题里那几个国家选择器是 Dawn 原生的，按钮渲染的是两截：
 *   {{ localization.country.name }} | {{ localization.country.currency.iso_code }}
 * 而这家店的 Shopify Markets 没有按市场配本地币种，所以
 * localization.available_countries 里每个国家的 currency 都是 USD
 * （于是 snippets/country-localization.liquid 里 show_currencies 算出来是 false，
 *  列表里每行的币种被 hidden 掉，按钮的币种那截永远写着 USD）。
 * 症状：换国家能成功，但币种卡在 USD。
 *
 * 店里的币种切换实际是 Hextom TMS 在做，它自己的浮层控件是好用的。
 * 这个文件的作用就是：让这些选择器去驱动同一套逻辑，做出「选意大利 = 切到 EUR」
 * 的效果，而**完全不动** TMS 那个浮层控件。
 *
 * 【覆盖哪几个】
 * 店里实际渲染出 3 个国家选择器：
 *   HeaderCountryForm        导航栏（桌面）
 *   FooterCountryForm        页脚
 *   HeaderCountryMobileForm  抽屉菜单（手机端）
 * 语言选择器天然不会被选中。
 *
 * ⚠️⚠️ 这里踩过一个很隐蔽的坑，改选择器之前务必先读这段 ⚠️⚠️
 *    原来用的是 form[id$="CountryForm"]（「以 CountryForm 结尾」）。
 *    但 HeaderCountryMobileForm 是以「…MobileForm」结尾的，**根本不匹配**：
 *          HeaderCountryForm        → 匹配 ✓
 *          FooterCountryForm        → 匹配 ✓
 *          HeaderCountryMobileForm  → 不匹配 ✗   （结尾是 yMobileForm）
 *    后果：手机端抽屉里那个选择器从来没被接管过，点国家走的是 Dawn 原生提交
 *    （只切国家、不带 tms 参数），于是「国家变成 Netherlands、币种还是 USD」。
 *    桌面端两个表单恰好都能匹配，所以在页脚点永远正常、在抽屉里点永远坏 ——
 *    表现出来很像「偶尔出问题」，其实是按位置分的。
 *
 *    现在改成按「action 指向 /localization」+「带 name="country_code" 隐藏域」
 *    两条来认，不再依赖 id 怎么写。语言表单带的是 locale_code，天然区分开。
 *
 * 【国家 → 币种 的对照从哪来】
 * 不自己维护汇率/对照表，直接读 TMS 吐在页面上的全局变量 window.tmsRegionData：
 *   { IT: { region_name:"Italy", currency_code:"EUR", currency_symbol:"€" }, ... }
 * 这份数据是 TMS 自己生成的，所以永远和它一致，它加一个国家我们就自动支持。
 * 注意是多对一：DE/FR/IT/ES/NL/IE 全都映射到 EUR。
 *
 * 【怎么触发切换】
 * TMS 没有公开的 setter（window.tms.selectorApi 官方文档写明是只读的）。
 * 它内部切换靠 URL 参数，代码里就是：
 *   setRegion(e)   → "tms-region"
 *   setCurrency(e) → "tms-currency"
 *   setLanguage(e) → "tms-language"
 * 拼完 URL 再 redirect()。这里就沿用这个参数 —— 已实测可用（价格变 €/£、
 * 浮层同步、且能持久化）。
 * ⚠️ 但这是 app 的**内部**实现，不在公开文档里，TMS 升级后有可能变。
 *    真要失效了，症状是「点了没反应」，届时改这里两个常量的字面值即可。
 *
 * 【为什么切换国家要另外 POST 一次】
 * Dawn 原生的做法是设置 hidden input 再 form.submit() POST 给 /localization，
 * 这一步切的是「国家」（影响市场/运费/语言），现在是能用的。
 * 我们的捕获监听会拦掉它的点击（否则它一提交就整页刷新，币种参数就丢了），
 * 所以这里要**手工把这一步补回来**，不能让它因为被接管而丢掉。
 * 顺序：先 fetch 提交国家 → 再带着 TMS 参数跳转，跳转后两者都生效。
 * 用的是被点击的那个表单本身，所以从哪个选择器点的就用哪个切。
 *
 * ⚠️ 这里踩过一个坑，改的时候别把 keepalive 去掉：
 *    下面这个 fetch 如果慢过 COUNTRY_POST_TIMEOUT_MS，看门狗会先跳转，而
 *    **导航会掐掉正在飞的 fetch**，country_code 就没写进 cookie。
 *    结果是币种按 URL 参数生效了、国家没生效 —— 按钮变成
 *    「United States | EUR €」这种对不上的组合，而且刷新也不会自愈，
 *    因为 TMS 已经记住了 EUR，Shopify 那边还停在旧国家。
 *    网络一慢就复现，所以症状是「偶尔」而不是「总是」。
 *    加 keepalive: true 之后这个请求在页面卸载后仍会跑完（sendBeacon 的 fetch 版），
 *    看门狗就可以安心地保住「不卡用户」这个前提。
 *
 * 【为什么只改按钮的「币种」那半截，不动国家名】
 * 这是个刻意的取舍，也是踩过坑之后的结论：
 *   - 国家名是**对的** —— 国家切换走 Shopify 原生 /localization，确实生效了
 *     （选 Italy 后按钮就显示 Italy）。Dawn 渲染的就是正确值，没必要覆盖。
 *   - 币种是**错的** —— 它来自 Shopify，而 Shopify 那边永远是 USD。
 * 所以只替换「|」后面的币种部分。这样就不再需要「先推出用户在哪个区域、再反查
 * 币种」—— 那套逻辑要维护一份「上次选了谁」的记录，一旦 TMS 自己切走了（比如按
 * IP 自动跳转），记录立刻过期，按钮就会写出一个跟实际价格对不上的币种。
 * 现在国家名来自服务端渲染、币种来自 TMS 的实时状态，两边都不会过期。
 *
 * 【币种值从哪读】
 * 优先读 TMS 浮层控件上写着的那一个 —— 那是用户肉眼能看到的、也是价格真正在用的：
 *   <div class="ht-tms-dropdown__selected" data-value="EUR">
 * 拿不到浮层（TMS 异步挂载，还没注入）时，退回用 selectorApi 的 region
 * 去 tmsRegionData 里反查币种。两条路都拿不到 → 不改按钮，保持 Dawn 默认。
 *
 * ⚠️ 没有采用 window.tms.selectorApi.currency 作币种来源：它在「已经切到 EUR」
 *    的状态下仍然报告 USD，疑似给的是店铺基准币而不是当前展示币。
 *    这一条**我没有直接验证过**（是从「按钮显示 Italy | USD $」这个现象反推的），
 *    但无论如何，浮层上的 data-value 都是更贴近用户所见、也更贴近实际价格的来源，
 *    所以选它；就算上面那个猜测是错的，这段代码的结果也照样是对的。
 *
 * 【默认值：United States | USD $】
 * 读不到 TMS 状态时（新访客、TMS 还没初始化完），这个脚本**什么都不改**，
 * 页面保持 Dawn 渲染出来的默认值，也就是 United States | USD $。
 * 所以不会出现「按钮写着 EUR 而价格其实是 $」这种自相矛盾的状态。
 *
 * 【国家是权威：币种对不上就把币种纠过来】（2026-10 新增）
 * 上面那套「国家名来自 Shopify、币种来自 TMS」有个后果：两边一旦不同步，
 * 按钮就会拼出现实中不存在的组合，典型是「United States | EUR €」。
 * 线上实测两条路都能掉进去，而且**都不会自愈**（刷新也没用）：
 *   1) 点国家时那次 POST 没落地（网络抖动、或页面被提前卸载）
 *      —— 币种按 URL 参数切了，Shopify 那边国家没切；
 *   2) TMS 的币种存在它自己的存储里，比 Shopify 的 localization cookie 活得久，
 *      cookie 一过期回到默认的 US，而 TMS 还记得 EUR。
 * 复现到的状态：按钮 "United States | EUR €"、价格 "€13,38 EUR"、cookie=US。
 *
 * 规则：**国家是权威**。真正决定市场/运费/税/结算的是 Shopify 的 country_code
 * （服务端就把它渲染在表单的 hidden 域里），所以币种永远向它看齐 ——
 * 启动时拿 country_code 去 tmsRegionData 查「应有币种」，和 TMS 当前币种不一致
 * 就带参数重新加载一次，把 TMS 拉回来。
 * 反过来做（按币种去改国家）会动到市场和运费，风险大得多，不做。
 *
 * ⚠️ 防跳转环：纠正要整页重载，万一重载后仍然对不上（比如 TMS 哪天不认这个参数了），
 *    就成了「每次加载都跳一次、永远跳」。所以用 sessionStorage 记一条
 *    「这个组合已经纠过」的一次性标记：纠过就不再纠；等两边真的对上（正常路径）
 *    再把标记清掉 —— 于是它只挡环，不影响下一次真正需要的纠正。
 *    存储读写不了（无痕模式）时**直接不纠**：宁可不修，也不冒跳转环的风险。
 *
 * ⚠️ 纠正时 tms-region 和 tms-currency **两个都要带**。实测只带 tms-currency 时，
 *    TMS 会自己挑一个代表性国家（给 EUR 就跳去 DE）—— 那等于把权威的国家改掉了。
 *
 * 【默认值不受影响】
 * 新访客：country_code=US、TMS 币种也是 USD → 一致 → 不重载，
 * 保持 United States | USD $。（TMS 的 IP 自动切币是关的，
 * 实测 ip_base_currency_code 为空串，所以不会按地区自动跳。）
 *
 * 【生效范围补充】
 * TMS 浮层控件（.ht-tms--float）完全不碰。
 */
(() => {
  'use strict';

  const FORM_SELECTOR = 'form[action*="/localization"]';
  const FLOAT_SELECTED_SELECTOR = '.ht-tms-dropdown__selected[data-value]';
  const REGION_PARAM = 'tms-region';
  const CURRENCY_PARAM = 'tms-currency';
  const POLL_FAST_MS = 120; // TMS 异步挂载期间：密集看几次
  const POLL_FAST_TIMES = 90; // 120ms × 90 ≈ 11s，够等 TMS 初始化完
  const POLL_SLOW_MS = 2000; // 之后转入长期低频（见 init 里的说明）
  const COUNTRY_POST_TIMEOUT_MS = 700; // 国家那次 POST 的等待上限，慢也不卡住用户
  const SYNC_GUARD_KEY = 'tms-country-bridge-synced'; // 防跳转环的一次性标记，见文件顶部
  const SWITCH_FLAG_KEY = 'tms-country-bridge-switching'; // 「刚点完国家」的落地页标记，见下方 isSwitchLanding
  // （这只是「等多久就先跳转」，不是「多久就放弃」—— fetch 带了 keepalive，
  //   提前跳转不会中断它，见文件顶部那段 ⚠️）

  /**
   * 页面上所有国家选择器的表单。
   * 识别依据是「带 name="country_code" 的隐藏域」，不是 id —— 详见文件顶部
   * 那段 ⚠️⚠️（按 id 后缀认会漏掉 HeaderCountryMobileForm）。
   * 语言选择器的隐藏域叫 locale_code，天然被排除。
   * @returns {HTMLFormElement[]}
   */
  function countryForms() {
    return Array.prototype.slice
      .call(document.querySelectorAll(FORM_SELECTOR))
      .filter((form) => form.querySelector('input[name="country_code"]'));
  }

  /** TMS 吐在页面上的区域表；没加载出来时返回 null */
  function getRegionData() {
    const data = window.tmsRegionData;
    return data && typeof data === 'object' ? data : null;
  }

  /** 从 TMS 的币种表里取符号（USD → "$"、CHF → "CHF"）；取不到返回空串 */
  function currencySymbol(code) {
    const dict = window.tmsSelectorData && window.tmsSelectorData.currency_language_selector_dict;
    const entry = (dict && dict[code]) || null;
    return (entry && entry.currency_symbol) || '';
  }

  /** 「EUR €」这样的展示文字；符号缺省或与代码相同时不重复（CHF 的符号就是 CHF） */
  function currencyLabel(code, symbol) {
    if (!code) return symbol || '';
    if (!symbol || symbol === code) return code;
    return `${code} ${symbol}`;
  }

  /**
   * TMS 当前真正在用的展示币种，取不到返回 null。
   * 优先读浮层控件上的 data-value —— 那是用户能看到、也是价格真正在用的值。
   * @returns {string|null} 形如 "EUR"
   */
  function currentDisplayCurrency() {
    const nodes = document.querySelectorAll(FLOAT_SELECTED_SELECTOR);
    for (let i = 0; i < nodes.length; i++) {
      const code = nodes[i].dataset.value;
      if (code && /^[A-Z]{3}$/.test(code)) return code; // 币种代码固定三个大写字母
    }

    // 浮层还没注入（TMS 异步挂载）时，用 region 反查一次
    const api = window.tms && window.tms.selectorApi;
    const region = api && api.region && api.region.code;
    const data = getRegionData();
    if (region && data && data[region]) return data[region].currency_code;

    return null;
  }

  /** 取出按钮文字里「|」之前的部分，也就是国家名 */
  function countryNameFrom(text) {
    const name = String(text || '')
      .split('|')[0]
      .replace(/\s+/g, ' ')
      .trim();
    return name || null;
  }

  /**
   * Shopify 那边的权威国家代码（服务端按 localization cookie 渲染进 hidden 域）。
   * 三个表单渲染的是同一个值，取第一个即可。
   * @returns {string|null} 形如 "US"
   */
  function shopifyCountry() {
    const form = countryForms()[0];
    const input = form && form.querySelector('input[name="country_code"]');
    const iso = input && input.value;
    return iso && /^[A-Z]{2}$/.test(iso) ? iso : null;
  }

  /**
   * 某个国家在 TMS 表里对应的币种。
   * 查不到（TMS 没覆盖这个国家）返回 null —— 拿不准就不动，别乱纠。
   * @param {string} iso
   * @returns {string|null}
   */
  function currencyForCountry(iso) {
    const data = getRegionData();
    const region = data && data[iso];
    return (region && region.currency_code) || null;
  }

  /**
   * 本页是不是「刚点完国家」的落地页？
   *
   * 需要这个判断的原因：点国家时，国家那次 POST 和跳转是并行的（见 switchTo），
   * 慢网络下 POST 可能还没落地，落地页服务端就已经按**旧国家**渲染好了 ——
   * 而 TMS 那边已经按 URL 参数切成了新币种。此时两边看起来就是「不一致」，
   * 但它是切换过程中的正常中间态，不是坏了。
   * 这时候要是去纠正，就会把用户刚选的国家顶回去（美国 ← 德国 这种回弹）。
   *
   * 所以：switchTo 跳转前打一个一次性标记，落地页读到就整页跳过纠正
   * （读完即清，下一次正常加载就恢复纠正能力）。真要是那次 POST 彻底丢了，
   * 用户翻到下一页时就会被纠正回来 —— 只是晚一页，代价可接受。
   *
   * 结果只判定一次并缓存：tick 会反复调用，不能每次都去读 storage。
   * @returns {boolean}
   */
  let switchLanding = null;
  function isSwitchLanding() {
    if (switchLanding !== null) return switchLanding;
    switchLanding = false;
    try {
      if (window.sessionStorage.getItem(SWITCH_FLAG_KEY) !== null) {
        window.sessionStorage.removeItem(SWITCH_FLAG_KEY);
        switchLanding = true;
      }
    } catch (e) { /* 拿不到 storage：当作不是，反正下面的纠正也会因为同样的原因跳过 */ }
    return switchLanding;
  }

  /**
   * 国家是权威：TMS 当前币种和「该国应有的币种」对不上时，带参数重载一次把 TMS 纠回来。
   * 只在两个值都拿得到时才判断（TMS 没初始化完就等下一轮 tick，不抢跑）。
   * 防跳转环的机制见文件顶部那段 ⚠️。
   */
  function syncCurrencyToCountry() {
    if (isSwitchLanding()) return; // 切换落地页：这时候的「不一致」是中间态，别纠（见上）
    const iso = shopifyCountry();
    const want = iso && currencyForCountry(iso);
    const have = currentDisplayCurrency();
    if (!iso || !want || !have) return; // 数据没齐：什么都不做，等下一轮

    if (want === have) {
      // 对上了 —— 撤掉一次性标记，这样这个会话里将来真需要纠正时还能再纠
      try {
        window.sessionStorage.removeItem(SYNC_GUARD_KEY);
      } catch (e) { /* 存不了也无所谓，标记本来就不存在 */ }
      return;
    }

    // 对不上。一次会话只纠一次：纠过（不管成没成）就不再纠，避免无限跳转。
    const key = `${iso}>${want}`;
    try {
      if (window.sessionStorage.getItem(SYNC_GUARD_KEY) === key) return;
      window.sessionStorage.setItem(SYNC_GUARD_KEY, key);
    } catch (e) {
      return; // 无痕模式等拿不到 storage：宁可不修，也不冒跳转环的风险
    }

    const target = new URL(window.location.href);
    target.searchParams.set(REGION_PARAM, iso); // 两个都带，见文件顶部 ⚠️
    target.searchParams.set(CURRENCY_PARAM, want);
    window.location.assign(target.toString());
  }

  /**
   * 把每个选择器按钮的币种那半截换成 TMS 当前的币种，国家名保持 Dawn 渲染的值。
   * 读不到 TMS 状态就什么都不做，保留 Dawn 的默认文字。
   */
  function syncButtonLabels() {
    const currency = currentDisplayCurrency();
    if (!currency) return;

    const label = currencyLabel(currency, currencySymbol(currency));

    countryForms().forEach((form) => {
      const span = form.querySelector('button.localization-form__select > span');
      if (!span) return;

      const name = countryNameFrom(span.textContent);
      if (!name) return;

      const text = `${name} | ${label}`;
      if (span.textContent.replace(/\s+/g, ' ').trim() === text) return; // 幂等：值相同不写回
      span.textContent = text;
    });
  }

  /**
   * 给每个列表的每一行补上该国家的币种。
   * Dawn 因为 show_currencies 为 false 把这些 span 都 hidden 掉了，
   * 这里用 TMS 的数据填上并显示出来 —— 对应「每个国家要对应自己的货币」。
   * （桌面端这些币种默认是 hover 才显形，Dawn 原本就这设计；
   *   想让它常驻可改 assets/component-localization-form.css 里
   *   .localization-form__currency 的 opacity: 0。）
   */
  function fillCurrencyColumns() {
    const data = getRegionData();
    if (!data) return;

    countryForms().forEach((form) => {
      const list = form.querySelector('.disclosure__list');
      if (!list || list.dataset.tmsCurrencyFilled === 'true') return;

      let filled = false;
      list.querySelectorAll('a[data-value]').forEach((link) => {
        const region = data[link.dataset.value];
        const span = link.querySelector('.localization-form__currency');
        if (!region || !span) return;

        span.textContent = currencyLabel(region.currency_code, currencySymbol(region.currency_code) || region.currency_symbol);
        span.classList.remove('hidden');
        filled = true;
      });

      if (!filled) return;

      // 多币种时 Dawn 会给列表加这个类（宽度放大到 25.5rem），跟着补上
      list.classList.add('country-selector__list--with-multiple-currencies');
      list.dataset.tmsCurrencyFilled = 'true';
    });
  }

  /**
   * 先按 Shopify 原生方式提交国家，再带着 TMS 参数跳转。
   * @param {string} iso 目标国家代码，如 "IT"
   * @param {string} currency 目标币种代码，如 "EUR"
   * @param {HTMLFormElement} form 被点击的那个选择器所在的表单
   */
  function switchTo(iso, currency, form) {
    const target = new URL(window.location.href);
    target.searchParams.set(REGION_PARAM, iso);
    target.searchParams.set(CURRENCY_PARAM, currency);

    let navigated = false;
    const go = () => {
      if (navigated) return;
      navigated = true;
      // 给落地页留个「这次切换是我发起的」标记，别让那边的纠正把刚选的国家顶回去
      try {
        window.sessionStorage.setItem(SWITCH_FLAG_KEY, iso);
      } catch (e) { /* 存不了就算了，最坏是慢网络下可能回弹一次 */ }
      window.location.assign(target.toString());
    };

    if (!form) {
      go();
      return;
    }

    const guard = window.setTimeout(go, COUNTRY_POST_TIMEOUT_MS);
    const body = new FormData(form);
    body.set('country_code', iso);

    fetch(form.getAttribute('action') || '/localization', {
      method: 'POST',
      body,
      credentials: 'same-origin',
      keepalive: true, // 页面卸载也不中断 —— 去掉它，慢网络下国家就会被丢掉
    }).then(
      () => {
        window.clearTimeout(guard);
        go();
      },
      () => {
        window.clearTimeout(guard);
        go();
      }
    );
  }

  /** 捕获阶段监听：抢在 Dawn 的 onItemClick 之前接管点击 */
  function onFormClick(event) {
    const form = event.currentTarget;
    const link = event.target.closest && event.target.closest('a[data-value]');
    if (!link || !form.contains(link)) return;

    const data = getRegionData();
    const iso = link.dataset.value;
    const region = data && data[iso];
    if (!region || !region.currency_code) return; // 认不出的区域：交回 Dawn 原生逻辑

    event.preventDefault();
    event.stopPropagation(); // Dawn 的 onItemClick 绑在 <a> 上、冒泡阶段，这里拦得住

    switchTo(iso, region.currency_code, form);
  }

  function bindForms() {
    countryForms().forEach((form) => {
      if (form.dataset.tmsCountryBridge === 'bound') return;
      form.dataset.tmsCountryBridge = 'bound';
      form.addEventListener('click', onFormClick, true);
    });
  }

  function init() {
    syncCurrencyToCountry(); // 最先跑：对不上就重载纠币种，别白渲染一屏
    bindForms();
    fillCurrencyColumns();
    syncButtonLabels();

    // 先密集轮询等 TMS 挂载完，之后转成长期低频继续盯着，而不是停掉。
    // 为什么不能停：TMS 浮层读不到时（比如它挂在 shadow root 里，
    // document.querySelector 够不着），唯一的信息源就只剩 selectorApi.region ——
    // 那是个 JS 对象属性，在 DOM 上没有任何痕迹，MutationObserver 看不见，
    // 只能定期去读。原来这里跑满 11 秒就 clearInterval，之后再变就跟不上了。
    // 2 秒一次的开销可以忽略，换来的是 TMS 什么时候变按钮都不会掉队。
    let ticks = 0;
    const tick = () => {
      syncCurrencyToCountry(); // 幂等（靠 sessionStorage 里的一次性标记）
      bindForms(); // 幂等（靠 dataset 里的标记），Dawn 重渲染出新的表单也能自动接管
      fillCurrencyColumns(); // 同上，每个列表内部有自己的「填过了」标记
      syncButtonLabels();
      ticks += 1;
      window.setTimeout(tick, ticks < POLL_FAST_TIMES ? POLL_FAST_MS : POLL_SLOW_MS);
    };
    window.setTimeout(tick, POLL_FAST_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
